// src/pages/SubscriptionManager.jsx (コーチングプラン追加版)
import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import { useUserProfile } from '../contexts/nobishiro-dashboard/useUserProfile';
import { Check, Crown, Zap, ArrowRight, AlertCircle, Users, User, Building2 } from 'lucide-react';
import proLineService from '../services/ProLineService';

const SubscriptionManager = () => {
  const { user } = useAuth();
  const { profile, loading: profileLoading } = useUserProfile(user?.uid);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [error, setError] = useState(null);
  const currentPlanId = profile?.subscription?.planId || 'free';

  const plans = [
    { id: 'free', name: 'Freeプラン', price: 0, period: '月', description: '気軽に始めたい方に', icon: <Check className="w-6 h-6" />, color: 'gray', features: ['NOBISHIRO能力診断 (月1回)', 'サマリーの基本閲覧', '10法則へのアクセス'], buttonText: '現在のプラン', popular: false },
    { id: 'student', name: 'Studentプラン', price: 480, period: '月', description: '学生・学習中の方に最適', icon: <Check className="w-6 h-6" />, color: 'blue', features: ['診断無制限', '基本的な学習計画', '20法則へのアクセス', 'リマインダー機能'], buttonText: 'Studentにアップグレード', popular: false, badge: '学生向け' },
    { id: 'standard', name: 'Standardプラン', price: 980, period: '月', description: '個人の成長を最大化', icon: <Crown className="w-6 h-6" />, color: 'indigo', features: ['Studentプランの全機能', 'AI分析（準備中・近日公開）', '成長パターン分析', '40法則へのアクセス', 'データエクスポート', '成長予測グラフ'], buttonText: 'Standardにアップグレード', popular: true, badge: '最人気' },
    { id: 'business', name: 'Businessプラン', price: 4980, period: '月', description: 'チームの成果を最大化', icon: <Zap className="w-6 h-6" />, color: 'purple', features: ['Standardプランの全機能', '4人セル制（リーダー1名＋メンバー3名）', 'チーム分析ダッシュボード', '1on1支援システム', 'AIコーチング', '優先サポート'], buttonText: 'Businessを選択', popular: false, badge: 'チーム向け' }
  ];

  // コーチングプラン（UnivaPayへ直接遷移）
  const coachingPlans = [
    {
      id: 'coaching_individual',
      name: '個別直接オンラインコーチング（富永 勇貴）',
      price: 30000,
      period: '月',
      description: '富永 勇貴による1対1のパーソナルコーチング',
      icon: <User className="w-6 h-6" />,
      color: 'amber',
      features: [
        'Businessプランの全機能',
        '1対1 個別オンラインセッション',
        'パーソナライズされた成長戦略',
        'AIコーチング 無制限',
        'チーム管理・分析',
        '専任サポート',
      ],
      url: 'https://univa.cc/C6d6JM',
      buttonText: '申し込む',
      badge: '個別',
    },
    {
      id: 'coaching_group',
      name: 'グループ直接オンラインコーチング',
      price: 50000,
      period: '月',
      description: '家族・組織向けグループコーチング',
      icon: <Users className="w-6 h-6" />,
      color: 'orange',
      features: [
        'Businessプランの全機能',
        'グループ オンラインセッション',
        '家族・組織向け成長支援',
        'AIコーチング 無制限',
        'チーム管理・分析',
        '専任サポート',
      ],
      url: 'https://univa.cc/A9_Zbc',
      buttonText: '申し込む',
      badge: '家族・組織',
    },
    {
      id: 'coaching_btob',
      name: '直接組織コーチング（法人BtoB）',
      price: 500000,
      period: '月',
      description: '法人・組織向け最上位コーチングプログラム',
      icon: <Building2 className="w-6 h-6" />,
      color: 'navy',
      features: [
        'Businessプランの全機能',
        '直接組織コーチング',
        '経営・組織戦略サポート',
        'AIコーチング 無制限',
        'チーム全員のアカウント管理',
        '月次レポート・分析',
        'カスタマイズ対応',
        '専任担当・優先対応',
      ],
      url: 'https://univa.cc/Lcz77X',
      buttonText: '法人申し込み',
      badge: 'ENTERPRISE',
      featured: true,
    },
  ];

  const handlePlanChange = async (planId) => {
    if (isProcessing || planId === currentPlanId || planId === 'free') return;
    setIsProcessing(true);
    setSelectedPlan(planId);
    setError(null);
    try {
      console.log('Plan change started:', planId);
      const result = await proLineService.createCheckoutSession(planId);
      if (result.success) {
        if (result.checkoutUrl) {
          window.location.href = result.checkoutUrl;
        } else if (result.isDemoMode) {
          alert(`${planId === 'pro' ? 'Pro' : 'Business'}プランへの変更が完了しました（デモモード）\nページをリロードして変更を確認してください。`);
          window.location.reload();
        }
      } else {
        throw new Error(result.message || '決済処理に失敗しました');
      }
    } catch (error) {
      console.error('Plan change error:', error);
      setError(`プランの変更に失敗しました: ${error.message}`);
      alert(`エラー: ${error.message}`);
    } finally {
      setIsProcessing(false);
      setSelectedPlan(null);
    }
  };

  const handleCoachingPlan = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('サブスクリプションをキャンセルしますか?\n\n現在の契約期限まで利用でき、その後フリープランに移行します。')) {
      return;
    }
    setIsProcessing(true);
    setError(null);
    try {
      console.log('Canceling subscription');
      const result = await proLineService.cancelSubscription(user.uid);
      if (result.success) {
        const expiryDate = new Date(result.expiryDate).toLocaleDateString('ja-JP', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        alert(`サブスクリプションをキャンセルしました。\n\n${expiryDate}まで現在のプランをご利用いただけます。`);
        window.location.reload();
      } else {
        throw new Error(result.message || 'キャンセルに失敗しました');
      }
    } catch (error) {
      console.error('Cancel error:', error);
      setError(`キャンセルに失敗しました: ${error.message}`);
      alert(`エラー: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  if (profileLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">プラン情報を読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-y-auto">
      <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto pb-12">

          {/* ページタイトル */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">プラン管理</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">あなたのビジネスと成長に最適なプランを選択してください</p>
          </div>

          {error && (
            <div className="mb-8 max-w-3xl mx-auto">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-red-900 mb-1">エラーが発生しました</h3>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* ── SaaS プラン ── */}
          <div className="mb-4">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest text-indigo-600 uppercase bg-indigo-50 border border-indigo-200 rounded-full px-4 py-1">
                SaaS Plans
              </span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-16">
            {plans.map((plan) => (
              <div key={plan.id} className={`relative pt-4 bg-white rounded-2xl shadow-lg border-2 transition-all duration-300 hover:shadow-xl ${plan.popular ? 'border-indigo-500 lg:scale-105' : currentPlanId === plan.id ? 'border-green-500' : 'border-gray-200 hover:border-indigo-300'}`}>
                <div className="absolute -top-4 inset-x-0 h-8 flex justify-center items-center gap-2">
                  {currentPlanId === plan.id && <span className="z-10 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium shadow-md">利用中</span>}
                  {plan.badge && <span className={`z-10 px-4 py-1 rounded-full text-sm font-medium text-white shadow-md ${plan.popular ? 'bg-indigo-500' : 'bg-purple-500'}`}>{plan.badge}</span>}
                </div>
                <div className="p-8 pt-4">
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`p-3 rounded-lg ${plan.color === 'indigo' ? 'bg-indigo-100 text-indigo-600' : plan.color === 'purple' ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-600'}`}>{plan.icon}</div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                      <p className="text-sm text-gray-600">{plan.description}</p>
                    </div>
                  </div>
                  <div className="mb-6">
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold text-gray-900">¥{plan.price.toLocaleString()}</span>
                      <span className="text-gray-600">/ {plan.period}</span>
                    </div>
                  </div>
                  <div className="mb-8">
                    <ul className="space-y-3">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button
                    onClick={() => handlePlanChange(plan.id)}
                    disabled={isProcessing || currentPlanId === plan.id || plan.id === 'free'}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 ${currentPlanId === plan.id || plan.id === 'free' ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : plan.popular ? 'bg-indigo-600 text-white hover:bg-indigo-700 transform hover:scale-105' : 'bg-gray-900 text-white hover:bg-gray-800'} ${isProcessing ? 'opacity-75 cursor-not-allowed' : ''}`}
                  >
                    {isProcessing && plan.id === selectedPlan ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        処理中...
                      </>
                    ) : currentPlanId === plan.id ? (
                      '現在のプラン'
                    ) : plan.id === 'free' ? (
                      'ダウングレード不可'
                    ) : (
                      <>
                        {plan.buttonText}
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* ── コーチングプラン ── */}
          <div className="mb-4">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest text-amber-700 uppercase bg-amber-50 border border-amber-200 rounded-full px-4 py-1">
                Direct Coaching Plans
              </span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
            <p className="text-sm text-gray-500 mb-6">富永 勇貴による直接オンラインコーチング。全プランにBusinessプランの機能が含まれます。</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
            {coachingPlans.map((plan) => (
              <div
                key={plan.id}
                className={`relative pt-4 bg-white rounded-2xl shadow-lg border-2 transition-all duration-300 hover:shadow-xl ${
                  plan.featured
                    ? 'border-blue-900 lg:scale-105'
                    : plan.color === 'amber'
                    ? 'border-amber-400 hover:border-amber-500'
                    : 'border-orange-400 hover:border-orange-500'
                }`}
              >
                {/* バッジ */}
                <div className="absolute -top-4 inset-x-0 h-8 flex justify-center items-center">
                  <span className={`z-10 px-4 py-1 rounded-full text-xs font-semibold text-white shadow-md ${
                    plan.featured ? 'bg-blue-900' : plan.color === 'amber' ? 'bg-amber-500' : 'bg-orange-500'
                  }`}>
                    {plan.badge}
                  </span>
                </div>

                <div className={`p-8 pt-4 ${plan.featured ? 'bg-gradient-to-b from-blue-950 to-blue-900 rounded-2xl text-white' : ''}`}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`p-3 rounded-lg ${
                      plan.featured
                        ? 'bg-blue-800 text-amber-400'
                        : plan.color === 'amber'
                        ? 'bg-amber-100 text-amber-600'
                        : 'bg-orange-100 text-orange-600'
                    }`}>
                      {plan.icon}
                    </div>
                    <div>
                      <h3 className={`text-lg font-bold ${plan.featured ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h3>
                      <p className={`text-sm ${plan.featured ? 'text-blue-300' : 'text-gray-500'}`}>{plan.description}</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <div className="flex items-baseline gap-2">
                      <span className={`text-4xl font-bold ${plan.featured ? 'text-white' : 'text-gray-900'}`}>
                        ¥{plan.price.toLocaleString()}
                      </span>
                      <span className={plan.featured ? 'text-blue-300' : 'text-gray-500'}>/ {plan.period}</span>
                    </div>
                  </div>

                  <div className="mb-8">
                    <ul className="space-y-3">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${plan.featured ? 'text-amber-400' : 'text-green-500'}`} />
                          <span className={`text-sm ${plan.featured ? 'text-blue-100' : 'text-gray-700'}`}>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button
                    onClick={() => handleCoachingPlan(plan.url)}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 transform hover:scale-105 ${
                      plan.featured
                        ? 'bg-amber-400 text-blue-900 hover:bg-amber-300 font-bold'
                        : plan.color === 'amber'
                        ? 'bg-amber-500 text-white hover:bg-amber-600'
                        : 'bg-orange-500 text-white hover:bg-orange-600'
                    }`}
                  >
                    {plan.buttonText}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* キャンセル管理 */}
          {currentPlanId !== 'free' && profile?.subscription?.status !== 'canceling' && (
            <div className="mt-12 max-w-3xl mx-auto">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">サブスクリプションの管理</h3>
                <p className="text-sm text-gray-600 mb-4">
                  サブスクリプションをキャンセルすると、現在の契約期限まで利用でき、その後フリープランに移行します。
                </p>
                <button
                  onClick={handleCancelSubscription}
                  disabled={isProcessing}
                  className="bg-red-600 text-white py-2 px-6 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  サブスクリプションをキャンセル
                </button>
              </div>
            </div>
          )}

          {profile?.subscription?.status === 'canceling' && profile?.subscription?.expiryDate && (
            <div className="mt-12 max-w-3xl mx-auto">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-yellow-900 mb-2">キャンセル予定</h3>
                    <p className="text-sm text-yellow-800 mb-2">
                      {new Date(profile.subscription.expiryDate).toLocaleDateString('ja-JP', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })} に現在のプランが終了し、フリープランに移行します。
                    </p>
                    <p className="text-sm text-yellow-700">
                      それまでは現在のプランの全機能をご利用いただけます。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-12 text-center text-sm text-gray-500">
            <p>プラン変更後、決済が完了するまで数分かかる場合があります。</p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SubscriptionManager;
